"use client";

import { useStatsigClient } from "./use-statsig-client";
import { useCallback } from "react";
import {
  type RegisteredEventName,
  type EventValueFor,
  type EventMetadataFor,
} from "../events/registry";
import { validateEvent } from "../events/validate";

/**
 * Typed Statsig event logger. Use this in new code instead of
 * `useStatsigClient().logEvent({...})` so the event name and metadata
 * are validated at compile time AND runtime against the registry.
 *
 * Existing call sites that use the raw `client.logEvent` continue to
 * work unchanged; runtime validation still applies via the server
 * pipeline (and via `_logEventRuntimeChecked` when needed on the client).
 *
 * @example
 *   const log = useLogEvent();
 *   log("subscription_canceled", "pediatric_support_single", {
 *     paywall_variant: "owlet_two_product",
 *     cancel_type: "free_trial",
 *   });
 */
export function useLogEvent() {
  // The enhanced client from this package, not the raw one from
  // `@statsig/react-bindings`. It injects the global metadata (route, surface,
  // entrypoint_id) and applies the visibility gate (PE-1775); reaching past it
  // to the raw client is how `useLogEvent` events previously escaped both.
  const { client, logEvent: logStatsigEvent } = useStatsigClient();

  return useCallback(
    function logEvent<N extends RegisteredEventName>(
      eventName: N,
      value?: EventValueFor<N>,
      metadata?: EventMetadataFor<N>
    ): void {
      const validation = validateEvent(eventName, value, metadata);
      if (validation.unregistered) {
        // Should be impossible because eventName is constrained to
        // RegisteredEventName at the type level. Defensive log only.
        console.warn(`[Statsig] Unregistered event: ${eventName}`);
      } else if (!validation.ok) {
        // Production: validateEvent already short-circuited. We let the
        // event fire so analytics isn't a SPOF, but log for observability.
        console.warn(
          `[Statsig] Event "${eventName}" failed validation:`,
          validation.issues
        );
      }

      if (!client) return;
      logStatsigEvent({
        eventName,
        // The Statsig SDK accepts string|number for value; coerce here
        // since EventValueFor may be richer.
        value: value as string | number | undefined,
        // The Statsig SDK accepts Record<string, string> for client-side
        // metadata. Coerce non-string values to strings — callers that
        // need numeric Sum metrics should rely on `value`.
        metadata: metadata
          ? Object.fromEntries(
              Object.entries(metadata).map(([k, v]) => [
                k,
                typeof v === "string" ? v : String(v),
              ])
            )
          : undefined,
      });
    },
    [client, logStatsigEvent]
  );
}
